/*
 * Copyright (c) 2025-2026.  atagon GmbH.  All rights reserved.
 *
 * This file is part of the atagon codebase and may not be copied,
 *  distributed, or modified without explicit written permission from
 * atagon GmbH. https://www.atagon.com
 */

import {create} from "zustand";
import Unique from "toolbox/unique";
import Time from "toolbox/time";

export const ERROR = "ERROR";
export const WARNING = "WARNING";
export const INFO = "INFO";

const MAX_NOTIFICATIONS = 200;

function buildNotification(type, input) {
    return {
        id: Unique.newInternalId(),
        type: type,
        data: input,
        timestamp: type === ERROR ? Math.floor(Date.now() / 1000) : Time.now(),
        environment: import.meta.env.MODE,
        isRead: false
    };
}

export const useNotificationStore = create((set, get) => ({
    notifications: [],

    createError(title, text = "", uiElementId = "") {
        const notification = buildNotification(ERROR, {
            title: title,
            text: text,
            uiElementId: uiElementId
        });
        set((state) => ({
            notifications: [...state.notifications, notification].slice(-MAX_NOTIFICATIONS)
        }));
        return notification;
    },

    createWarning(title, text = "", uiElementId = "") {
        const notification = buildNotification(WARNING, {
            title: title,
            text: text,
            uiElementId: uiElementId
        });
        set((state) => ({
            notifications: [...state.notifications, notification].slice(-MAX_NOTIFICATIONS)
        }));
        return notification;
    },

    createInfo(title, text = "", uiElementId = "") {
        const notification = buildNotification(INFO, {
            title: title,
            text: text,
            uiElementId: uiElementId
        });
        set((state) => ({
            notifications: [...state.notifications, notification].slice(-MAX_NOTIFICATIONS)
        }));
        return notification;
    },

    markRead(messageId) {
        set((state) => ({
            notifications: state.notifications.map((message) =>
                message.id === messageId
                    ? {...message, isRead: true}
                    : message
            )
        }));
    },

    clear(uiElementId) {
        set((state) => ({
            notifications: state.notifications.map((message) =>
                message.data?.uiElementId === uiElementId
                    ? {...message, isRead: true}
                    : message
            )
        }));
    },

    getAll() {
        return get().notifications;
    },

    onChange(callback) {
        return useNotificationStore.subscribe(() => {
            callback();
        });
    }
}));

export const getNotificationStore = () => useNotificationStore.getState();